import { Accordion, AccordionStylesNames, Box, Flex, MantineStyleProp, Text } from '@mantine/core';
import { ReactNode } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type SectionCardProps = {
  value: string;
  title: string;
  titleAdornment?: ReactNode;
  count: number;
  countNoun?: { one: string; many: string };
  showCount?: boolean;
  titleMt?: number;
  rightSection: ReactNode;
  rightSectionPlacement?: 'overlay' | 'inline';
  variant?: string;
  bg?: string;
  chevron?: ReactNode;
  styles?: Partial<Record<AccordionStylesNames, React.CSSProperties>>;
  style?: MantineStyleProp;
  openValue?: string | null;
  onOpenChange?: (value: string | null) => void;
  controlTestId?: string;
  countTestId?: string;
  panelTestId?: string;
  children: ReactNode;
};

const DEFAULT_COUNT_NOUN = { one: 'source', many: 'sources' };

export default function SectionCard({
  value,
  title,
  titleAdornment,
  count,
  countNoun = DEFAULT_COUNT_NOUN,
  showCount = true,
  titleMt,
  rightSection,
  rightSectionPlacement = 'overlay',
  variant,
  bg,
  chevron,
  styles,
  style,
  openValue,
  onOpenChange,
  controlTestId,
  countTestId,
  panelTestId,
  children,
}: SectionCardProps) {
  const heading = (
    <>
      {showCount && (
        <Text fw={FONT_WEIGHTS.semibold} size="xs" c="gray.4" data-testid={countTestId}>
          {count} {count === 1 ? countNoun.one : countNoun.many}
        </Text>
      )}
      {titleAdornment ? (
        <Flex align="center" gap="xs" mt={titleMt}>
          <Text fw={FONT_WEIGHTS.bold} size="lg" c="gray.10">
            {title}
          </Text>
          {titleAdornment}
        </Flex>
      ) : (
        <Text fw={FONT_WEIGHTS.bold} size="lg" c="gray.10" mt={titleMt}>
          {title}
        </Text>
      )}
    </>
  );

  const controlledProps =
    openValue !== undefined ? { value: openValue, onChange: onOpenChange } : {};

  return (
    <Accordion
      variant={variant}
      chevron={chevron}
      bg={bg}
      styles={styles}
      style={style}
      {...controlledProps}
    >
      <Accordion.Item value={value} role="region">
        {rightSectionPlacement === 'overlay' ? (
          // Control and right section share one grid cell, so a right section taller than the
          // heading grows the header instead of being clipped. The right section lies over the
          // control, so it passes clicks through to it; whatever in it needs clicks takes them back.
          <Box pos="relative" style={{ display: 'grid' }}>
            <Accordion.Control data-testid={controlTestId} style={{ gridArea: '1 / 1' }}>
              {heading}
            </Accordion.Control>
            <Box
              style={{
                gridArea: '1 / 1',
                justifySelf: 'end',
                alignSelf: 'center',
                pointerEvents: 'none',
              }}
            >
              {rightSection}
            </Box>
          </Box>
        ) : (
          <Accordion.Control data-testid={controlTestId}>
            <Flex justify="space-between" align="center">
              <Box>{heading}</Box>
              {rightSection}
            </Flex>
          </Accordion.Control>
        )}
        <Accordion.Panel data-testid={panelTestId}>{children}</Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}
